// src/pages/Admin/VerifiedPhoneNumbersPage.tsx

import React, { useEffect, useState } from "react";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Import UI components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Import API
import { api } from "@/lib/api";

// Import types
import { VerifiedPhoneNumber } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const AddVerifiedPhoneNumberSchema = z.object({
  phone_number: z.string().nonempty("Phone number is required"),
});

type AddVerifiedPhoneNumberValues = z.infer<
  typeof AddVerifiedPhoneNumberSchema
>;

function VerifiedPhoneNumbersPage() {
  const { selectedOrganizationId } = useOrganizationsStore();
  const [verifiedPhoneNumbers, setVerifiedPhoneNumbers] = useState<
    VerifiedPhoneNumber[]
  >([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const form = useForm<AddVerifiedPhoneNumberValues>({
    resolver: zodResolver(AddVerifiedPhoneNumberSchema),
    defaultValues: {
      phone_number: "",
    },
  });

  useEffect(() => {
    if (selectedOrganizationId) {
      fetchVerifiedPhoneNumbers(selectedOrganizationId);
    }
  }, [selectedOrganizationId]);

  const fetchVerifiedPhoneNumbers = async (organizationId: string) => {
    try {
      const numbers = await api.getVerifiedPhoneNumbers(organizationId);
      setVerifiedPhoneNumbers(numbers);
    } catch (error) {
      console.error("Failed to fetch verified phone numbers:", error);
      toast({
        title: "Failed to load verified phone numbers",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: AddVerifiedPhoneNumberValues) => {
    if (!selectedOrganizationId) return;
    try {
      const newNumber = await api.verifyPhoneNumber(
        selectedOrganizationId,
        data.phone_number
      );
      setVerifiedPhoneNumbers((prev) => [...prev, newNumber]);
      toast({ title: "Phone number added successfully" });
      form.reset();
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error("Failed to add verified phone number:", error);
      toast({
        title: "Failed to add verified phone number",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  if (!selectedOrganizationId) {
    return <div>Please select an organization.</div>;
  }

  return (
    <Card className="m-8">
      <CardHeader className="flex flex-row justify-between items-center mb-4">
        <CardTitle>Verified Phone Numbers</CardTitle>
        <div className="flex justify-between items-center mb-4">
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>Add Verified Phone Number</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Verified Phone Number</DialogTitle>
                <DialogDescription>
                  Enter the phone number you wish to verify.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="+1234567890"
                        {...form.register("phone_number")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                  <DialogFooter>
                    <Button type="submit">Verify Phone Number</Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Phone Number</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Daily.co ID</TableHead>
              <TableHead>Created At</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {verifiedPhoneNumbers.map((number) => (
              <TableRow key={number.id}>
                <TableCell>{number.phone_number}</TableCell>
                <TableCell>{number.id}</TableCell>
                <TableCell>{number.daily_co_phone_number_id}</TableCell>
                <TableCell>
                  {new Date(number.created_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default VerifiedPhoneNumbersPage;
